/* Contacten (/crm/contacts): één compacte, doorzoekbare lijst, zoals Organisaties
 * (ADR 0022). Geen bulkverwijderen meer: archiveren gaat per contact en alleen voor
 * beheerders.
 *
 * Tabs volgen de organisatie van het contact: met project, Charging, leveranciers,
 * zonder organisatie. Per contact: naam en functie, organisatie, e-mail en telefoon;
 * mailen en bellen naast de rij. */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Pencil, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  beginRichting, contactLijst, filterEnSorteer, filterNaarUrl, tellingen, TAB_LABEL,
  type LijstContact, type LijstFilter, type Sortering,
} from './data/contacten';
import { LijstStaat, OrgIcoon, SorteerKop, Tabs, useLijstToetsen, Voet, Werkbalk } from './ui/Lijst';
import { Archiveer } from './ui/Archiveer';

const KOLOMMEN =
  'grid-cols-[minmax(0,1fr)] sm:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)] lg:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1fr)_150px]';

interface Props {
  contacten: LijstContact[];
  laden: boolean;
  fout: Error | null;
  filter: LijstFilter;
  onFilter: (f: LijstFilter) => void;
  magBewerken: boolean;
  magBeheren: boolean;
  onNieuw: () => void;
  onBewerk: (c: LijstContact) => void;
  onArchiveer: (c: LijstContact) => void;
}

export function ContactLijst(props: Props) {
  const { contacten, laden, fout, filter, onFilter } = props;
  const navigate = useNavigate();
  const zoekRef = useRef<HTMLInputElement>(null);
  const rijRefs = useRef<(HTMLAnchorElement | null)[]>([]);
  const [actief, setActief] = useState(-1);

  const zichtbaar = useMemo(() => filterEnSorteer(contacten, filter), [contacten, filter]);
  const tabs = useMemo(() => tellingen(contacten, filter.zoek).map((t) => ({ ...t, label: TAB_LABEL[t.tab] })), [contacten, filter.zoek]);

  useEffect(() => setActief(-1), [filter.zoek, filter.tab]);
  useEffect(() => {
    if (actief >= 0) rijRefs.current[actief]?.scrollIntoView({ block: 'nearest' });
  }, [actief]);
  useEffect(() => {
    if (!laden) contactLijst.bewaar(new URLSearchParams(filterNaarUrl(filter)).toString(), zichtbaar.map((c) => c.id));
  }, [zichtbaar, laden, filter]);

  const open = useCallback((i: number) => {
    const c = zichtbaar[i];
    if (c) navigate(`/crm/contacts/${c.id}`);
  }, [zichtbaar, navigate]);
  const wis = useCallback(() => onFilter({ ...filter, zoek: '' }), [filter, onFilter]);
  useLijstToetsen({ zoekRef, aantal: zichtbaar.length, actief, setActief, open, wis });

  const sorteer = (s: Sortering) =>
    onFilter({ ...filter, sortering: s, oplopend: filter.sortering === s ? !filter.oplopend : beginRichting(s) });
  const kop = { huidig: filter.sortering, oplopend: filter.oplopend, onSorteer: sorteer };
  /* mailen en bellen altijd; bewerken en archiveren naar recht */
  const breedte = props.magBeheren ? 'w-[148px]' : props.magBewerken ? 'w-[112px]' : 'w-[76px]';

  return (
    <div className="flex flex-col gap-4">
      <Werkbalk
        ref={zoekRef}
        titel="Contacten"
        zoek={filter.zoek}
        onZoek={(zoek) => onFilter({ ...filter, zoek })}
        placeholder="Zoek naam, organisatie of e-mail"
        nieuw={props.magBewerken ? { label: 'Nieuw contact', onKlik: props.onNieuw } : null}
      />
      <Tabs tabs={tabs} label="Organisatie" actief={filter.tab} onKies={(tab) => onFilter({ ...filter, tab })}
        stip={{ projecten: 'bg-green-600', charging: 'bg-sky-500', leveranciers: 'bg-violet-500', zonder: 'bg-zinc-400' }} />

      {fout || laden || zichtbaar.length === 0 ? (
        <LijstStaat
          fout={fout}
          laden={laden}
          wat="Contacten"
          leeg={
            <>
              {contacten.length === 0 ? 'Nog geen contacten.' : 'Geen contacten gevonden.'}{' '}
              {(filter.zoek || filter.tab !== 'alle') && contacten.length > 0 && (
                <button type="button" className="font-medium text-foreground underline" onClick={() => onFilter({ ...filter, zoek: '', tab: 'alle' })}>
                  Toon alles
                </button>
              )}
            </>
          }
        />
      ) : (
        <div className="overflow-hidden rounded-xl border bg-background">
          <div className="flex items-center border-b bg-muted/40 text-xs text-muted-foreground">
            <div className={cn('grid flex-1 items-center gap-3 py-2 pl-4 pr-2', KOLOMMEN)}>
              <SorteerKop s="naam" {...kop}>Naam</SorteerKop>
              <span className="hidden sm:block"><SorteerKop s="organisatie" {...kop}>Organisatie</SorteerKop></span>
              <span className="hidden lg:block">E-mail</span>
              <span className="hidden xl:block">Telefoon</span>
            </div>
            <span className={cn(breedte, 'shrink-0')} />
          </div>
          <ul className="divide-y">
            {zichtbaar.map((c, i) => (
              <li key={c.id} data-contact-rij={c.id} className={cn('group flex items-center', i === actief && 'bg-muted')}>
                <Link
                  ref={(el) => (rijRefs.current[i] = el)}
                  to={`/crm/contacts/${c.id}`}
                  onMouseEnter={() => setActief(i)}
                  aria-label={`${c.naam} openen`}
                  className={cn('grid flex-1 items-center gap-3 py-2.5 pl-4 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', KOLOMMEN)}
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">{c.naam}</span>
                    {(c.functie || c.organisatie) && (
                      <span className="truncate text-xs text-muted-foreground">
                        {c.functie}
                        {/* op smalle schermen staat de organisatie hier, anders in haar eigen kolom */}
                        {c.organisatie && <span className="sm:hidden">{c.functie ? ' · ' : ''}{c.organisatie.naam}</span>}
                      </span>
                    )}
                  </span>
                  <span className="hidden min-w-0 items-center gap-2 sm:flex">
                    {c.organisatie ? (
                      <>
                        <OrgIcoon src={c.organisatie.icoon} naam={c.organisatie.naam} className="h-5 w-5" />
                        <span className="truncate">{c.organisatie.naam}</span>
                        {c.organisatie.gearchiveerd && <span className="shrink-0 text-xs text-amber-700">(gearchiveerd)</span>}
                      </>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </span>
                  <span className="hidden truncate text-muted-foreground lg:block">{c.email ?? '—'}</span>
                  <span className="hidden truncate tabular-nums text-muted-foreground xl:block">{c.telefoon ?? '—'}</span>
                </Link>
                <span className={cn('flex shrink-0 items-center justify-end gap-0.5 pr-2', breedte)}>
                  {c.email ? (
                    <a href={`mailto:${c.email}`} aria-label={`${c.naam} mailen`} title={c.email} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
                      <Mail className="h-4 w-4" />
                    </a>
                  ) : <span className="h-8 w-8" />}
                  {c.telefoon ? (
                    <a href={`tel:${c.telefoon.replace(/[^\d+]/g, '')}`} aria-label={`${c.naam} bellen`} title={c.telefoon} className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
                      <Phone className="h-4 w-4" />
                    </a>
                  ) : <span className="h-8 w-8" />}
                  <span className="flex opacity-100 sm:opacity-0 sm:transition-opacity sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                    {props.magBewerken && (
                      <Button variant="ghost" size="icon" aria-label="Bewerken" title="Bewerken" className="h-8 w-8 text-muted-foreground" onClick={() => props.onBewerk(c)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                    {props.magBeheren && (
                      <Archiveer wat="contact" naam={c.naam} bezwaar={null} onArchiveer={() => props.onArchiveer(c)} />
                    )}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <Voet aantal={zichtbaar.length} enkelvoud="contact" meervoud="contacten" />
        </div>
      )}
    </div>
  );
}
